/**
 * AuthGate — the route guard wrapping every authenticated console route.
 *
 * Enforces the full console access predicate `user && platformUser && mfaPassed`
 * (separate auth realm, MFA obligatori). The states, in order:
 *
 *   1. `loading`            → the initial session + platform-user lookup is
 *                             resolving: show a neutral loading view.
 *   2. `notAuthorized`      → a signed-in auth account with NO platform_users row
 *                             (a console user has no public.users row by design):
 *                             show "No autoritzat" + sign out.
 *   3. signed out           → redirect to /login.
 *   4. signed in, MFA not   → render <MfaGate /> (enrollment or TOTP challenge).
 *      yet satisfied
 *   5. fully authorized     → render the protected children.
 *
 * `mfaPassed` lives only in memory (AuthProvider), so a reload always re-runs the
 * MFA challenge before the children mount.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from '@tanstack/react-router';
import { Button, Card, useTheme } from '@obrava/ui';
import { CenteredCanvas, LoadingView, SectionTitle } from '../components/layout.js';
import { Banner } from '../components/Banner.js';
import { MfaGate } from './MfaGate.js';
import { useAuth } from './AuthProvider.js';

/** Shown when a signed-in account is not a platform super-user. */
function NotAuthorizedView(): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const { signOut } = useAuth();
  return (
    <CenteredCanvas>
      <Card>
        <SectionTitle>{t('platformConsole.auth:notAuthorizedTitle')}</SectionTitle>
        <Banner tone="danger" style={{ marginBottom: theme.spacing.space[6] }}>
          {t('platformConsole.auth:notAuthorizedBody')}
        </Banner>
        <Button fullWidth variant="secondary" onClick={() => void signOut()}>
          {t('platformConsole.auth:backToSignIn')}
        </Button>
      </Card>
    </CenteredCanvas>
  );
}

export function AuthGate(props: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  const { user, platformUser, loading, mfaPassed, notAuthorized } = useAuth();

  if (loading) {
    return <LoadingView label={t('common:loading')} />;
  }

  if (notAuthorized) {
    return <NotAuthorizedView />;
  }

  // Signed out (or session dropped): bounce to the login screen.
  if (!user || !platformUser) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but the mandatory second factor is not satisfied this session.
  if (!mfaPassed) {
    return <MfaGate />;
  }

  return <>{props.children}</>;
}
